import { useState, useCallback, useEffect } from 'react';
import type { PreChatAnswer } from '../types/events';

/**
 * Session data stored in localStorage.
 */
interface SessionData {
  sessionId: string;
  routerId: string;
  createdAt: number;
  /** The location this visitor picked when the widget opened, if they did (CAIV-677). */
  locationId?: number | null;
}

/**
 * Hook return type for useSession.
 */
export interface UseSessionReturn {
  /** Current session ID, or null if no session exists */
  sessionId: string | null;
  /** Whether a session exists that can be restored */
  hasSession: boolean;
  /** Save a new session ID */
  saveSession: (sessionId: string) => void;
  /** Clear the current session */
  clearSession: () => void;
  /** Get the session ID for restoration (returns null if expired or invalid) */
  getRestorationSessionId: () => string | null;
  /** The location picked in this session, or null when none was ever picked. */
  getLocationId: () => number | null;
  /** Remember the picked location so the question is asked once, not on every open. */
  saveLocationId: (locationId: number) => void;
  /** Contact details given to the pre-chat form, or null when never answered. */
  getContactDetails: () => PreChatAnswer | null;
  /** Remember them, so a returning visitor is not asked for the same thing twice. */
  saveContactDetails: (details: PreChatAnswer) => void;
}

const STORAGE_KEY_PREFIX = 'clubplanner_widget_session_';
const CONTACT_KEY_PREFIX = 'clubplanner_widget_contact_';
// Rolling idle window: createdAt is refreshed every time the session is saved
// (i.e. on each (re)connect), so this is "time since the widget was last
// opened", not an absolute cap. Kept short so a returning visitor on a shared
// computer does not pick up the previous person's conversation. Mirrors the
// backend session TTL (SessionManager.DEFAULT_SESSION_TTL).
const SESSION_EXPIRY_MS = 2 * 60 * 60 * 1000; // 2 hours

/**
 * Get the storage key for a specific router.
 */
function getStorageKey(routerId: string): string {
  return `${STORAGE_KEY_PREFIX}${routerId}`;
}

/**
 * Contact details live beside the session rather than inside it (CAIV-362).
 *
 * The session blob expires after two hours and is cleared whenever it cannot be restored.
 * Being asked for your name and number again because you came back the next day reads as
 * the club not listening, so this outlives it. Keyed per router, so one club's answer is
 * never offered to another.
 */
function getContactKey(routerId: string): string {
  return `${CONTACT_KEY_PREFIX}${routerId}`;
}

/**
 * Safely parse session data from localStorage.
 */
function parseSessionData(json: string | null): SessionData | null {
  if (!json) return null;

  try {
    const data = JSON.parse(json) as SessionData;
    // Validate required fields
    if (!data.sessionId || !data.routerId || !data.createdAt) {
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * Check if a session is expired.
 */
function isSessionExpired(data: SessionData): boolean {
  const now = Date.now();
  return now - data.createdAt > SESSION_EXPIRY_MS;
}

/**
 * Hook for managing session persistence in localStorage.
 *
 * Sessions are stored per-router and expire after 24 hours.
 * This allows users to restore their conversation history when
 * returning to the widget.
 *
 * @param routerId - The ID of the router this session is for
 * @returns Session management functions and state
 */
export function useSession(routerId: string): UseSessionReturn {
  const storageKey = getStorageKey(routerId);
  const contactKey = getContactKey(routerId);

  // Initialize state from localStorage
  const [sessionId, setSessionId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;

    const json = localStorage.getItem(storageKey);
    const data = parseSessionData(json);

    if (!data) return null;
    if (data.routerId !== routerId) return null;
    if (isSessionExpired(data)) {
      // Clean up expired session
      localStorage.removeItem(storageKey);
      return null;
    }

    return data.sessionId;
  });

  // Sync state if routerId changes
  useEffect(() => {
    const json = localStorage.getItem(storageKey);
    const data = parseSessionData(json);

    if (!data || data.routerId !== routerId || isSessionExpired(data)) {
      setSessionId(null);
      if (data) {
        localStorage.removeItem(storageKey);
      }
    } else {
      setSessionId(data.sessionId);
    }
  }, [routerId, storageKey]);

  /**
   * Save a new session ID to localStorage.
   */
  const saveSession = useCallback(
    (newSessionId: string) => {
      // Carry the picked location across: this runs on every (re)connect to refresh the
      // rolling window, and dropping it here would re-ask the question on each reconnect.
      const existing = parseSessionData(localStorage.getItem(storageKey));
      const data: SessionData = {
        sessionId: newSessionId,
        routerId,
        createdAt: Date.now(),
        locationId:
          existing && existing.routerId === routerId && !isSessionExpired(existing)
            ? (existing.locationId ?? null)
            : null,
      };

      try {
        localStorage.setItem(storageKey, JSON.stringify(data));
        setSessionId(newSessionId);
      } catch (error) {
        // localStorage might be full or disabled
        console.warn('[ClubPlannerWidget] Failed to save session:', error);
        setSessionId(newSessionId);
      }
    },
    [routerId, storageKey]
  );

  /**
   * Clear the current session from localStorage.
   */
  const clearSession = useCallback(() => {
    localStorage.removeItem(storageKey);
    setSessionId(null);
  }, [storageKey]);

  /**
   * Get the session ID for restoration.
   * Returns null if no valid session exists or if it's expired.
   */
  const getRestorationSessionId = useCallback((): string | null => {
    const json = localStorage.getItem(storageKey);
    const data = parseSessionData(json);

    if (!data) return null;
    if (data.routerId !== routerId) return null;
    if (isSessionExpired(data)) {
      localStorage.removeItem(storageKey);
      return null;
    }

    return data.sessionId;
  }, [routerId, storageKey]);

  const getLocationId = useCallback((): number | null => {
    const data = parseSessionData(localStorage.getItem(storageKey));
    if (!data || data.routerId !== routerId || isSessionExpired(data)) return null;
    return data.locationId ?? null;
  }, [routerId, storageKey]);

  /**
   * Remember the picked location against the current session.
   *
   * Stored client-side as well as server-side because the question is asked before there is a
   * conversation: without this a reload would ask again, and the visitor would reasonably read
   * that as the club not listening.
   */
  const saveLocationId = useCallback(
    (locationId: number) => {
      const existing = parseSessionData(localStorage.getItem(storageKey));
      if (!existing || existing.routerId !== routerId) return;
      try {
        localStorage.setItem(storageKey, JSON.stringify({ ...existing, locationId }));
      } catch (error) {
        console.warn('[ClubPlannerWidget] Failed to save picked location:', error);
      }
    },
    [routerId, storageKey]
  );

  const getContactDetails = useCallback((): PreChatAnswer | null => {
    try {
      const raw = localStorage.getItem(contactKey);
      return raw ? (JSON.parse(raw) as PreChatAnswer) : null;
    } catch {
      // Private windows and blocked site data throw. Asking again is worse than remembering
      // and better than a widget that will not open.
      return null;
    }
  }, [contactKey]);

  const saveContactDetails = useCallback(
    (details: PreChatAnswer) => {
      try {
        localStorage.setItem(contactKey, JSON.stringify(details));
      } catch (error) {
        console.warn('[ClubPlannerWidget] Failed to save contact details:', error);
      }
    },
    [contactKey]
  );

  return {
    sessionId,
    hasSession: sessionId !== null,
    saveSession,
    clearSession,
    getRestorationSessionId,
    getLocationId,
    saveLocationId,
    getContactDetails,
    saveContactDetails,
  };
}

export default useSession;
